import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabase";
import { shouldSkipSupabase } from "../../helpers/guestMode";
import {
  listFinanceCategories,
  newFinanceIdempotencyKey,
} from "../../helpers/financeActivity";
import { listFinanceAccounts } from "../../helpers/financeAccounts";
import {
  createManualInboundEvent,
  createManualOutboundEvent,
} from "../../helpers/financeIngestion";
import { resolveFinanceOrganizationIdFromActor } from "../../helpers/nexoraOpsAccess";
import { usePermissions } from "../../hooks/usePermissions";
import { CPDropdown } from "../CPDropdown";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { FinanceFormDesktop } from "./FinanceDesktop";
import {
  FinanceBackButton,
  FINANCE_CARD_STYLE,
  FINANCE_HEADER_STYLE,
  FINANCE_INPUT_STYLE,
  FINANCE_LABEL_STYLE,
  FINANCE_PRIMARY_BTN_STYLE,
  FINANCE_SCREEN_STYLE,
  FINANCE_SECONDARY_BTN_STYLE,
  FINANCE_SUBTITLE_STYLE,
  FINANCE_TITLE_STYLE,
} from "./financeUi";

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Money In / Money Out — posts via Finance Ingestion Contract only.
 * @param {"in"|"out"} mode
 */
export function FinanceMoneyFlowScreen({
  mode = "in",
  currentUser,
  onBack,
  onSuccess,
}) {
  const isInbound = mode === "in";
  const title = isInbound ? "Money In" : "Money Out";
  const subtitle = isInbound
    ? "Record money coming into an account."
    : "Record money leaving an account.";

  const isDesktop = useIsDesktopViewport();
  const { actor } = usePermissions();
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [accountId, setAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [occurredOn, setOccurredOn] = useState(todayIsoDate());
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    if (shouldSkipSupabase(currentUser)) {
      setAccounts([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [accountRows, categoryRows] = await Promise.all([
        listFinanceAccounts(supabase, { includeInactive: false }),
        listFinanceCategories(supabase),
      ]);
      setAccounts(accountRows);
      setCategories(categoryRows);
      setError(null);
    } catch (err) {
      console.error("[Finance] money flow load failed:", err);
      setError("Unable to load accounts.");
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!accountId && accounts[0]?.id) {
      setAccountId(accounts[0].id);
    }
  }, [accounts, accountId]);

  const filteredCategories = useMemo(() => {
    const wanted = isInbound ? "income" : "expense";
    return categories.filter(
      (category) => category.type === wanted || category.type === "other",
    );
  }, [categories, isInbound]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const organizationId = resolveFinanceOrganizationIdFromActor(
        actor || currentUser,
      );
      if (!organizationId) {
        throw new Error("Unable to resolve organization.");
      }
      if (!accountId) {
        throw new Error("Select an account.");
      }
      const amountValue = Number(amount);
      if (!Number.isFinite(amountValue) || amountValue <= 0) {
        throw new Error("Enter an amount greater than zero.");
      }

      const payload = {
        organization_id: organizationId,
        account_id: accountId,
        amount: amountValue,
        occurred_on: occurredOn || todayIsoDate(),
        description: description.trim() || title,
        notes: notes.trim() || null,
        category_id: categoryId || null,
        idempotency_key: newFinanceIdempotencyKey(
          isInbound ? "money-in" : "money-out",
        ),
      };

      if (isInbound) {
        await createManualInboundEvent(supabase, payload);
      } else {
        await createManualOutboundEvent(supabase, payload);
      }

      if (typeof onSuccess === "function") {
        onSuccess();
      } else if (typeof onBack === "function") {
        onBack();
      }
    } catch (err) {
      console.error("[Finance] money flow save failed:", err);
      setError(err?.message || "Unable to save.");
    } finally {
      setBusy(false);
    }
  };

  if (isDesktop) {
    return (
      <FinanceFormDesktop
        title={title}
        subtitle={subtitle}
        onBack={onBack}
        error={error}
        loading={loading}
        onSubmit={handleSubmit}
        submitLabel={`Save ${title}`}
        submitDisabled={busy || accounts.length === 0}
        busy={busy}
        onCancel={onBack}
        hint={accounts.length === 0 ? "Create an account first before recording money." : null}
      >
        <CPDropdown
          label="Account"
          value={accountId}
          onChange={setAccountId}
          required={true}
          options={[
            { value: "", label: "Select account" },
            ...accounts.map((account) => ({ value: account.id, label: account.name })),
          ]}
        />
        <label className="fin-field">
          <span>Amount</span>
          <input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required />
        </label>
        <label className="fin-field">
          <span>Date</span>
          <input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} required />
        </label>
        <CPDropdown
          label="Category (optional)"
          value={categoryId}
          onChange={setCategoryId}
          options={[
            { value: "", label: "None" },
            ...filteredCategories.map((category) => ({ value: category.id, label: category.name })),
          ]}
        />
        <label className="fin-field fin-form__full">
          <span>Description</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              isInbound
                ? "Customer payment, deposit, interest…"
                : "Office expense, fuel, subscription…"
            }
          />
        </label>
        <label className="fin-field fin-form__full">
          <span>Notes (optional)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </FinanceFormDesktop>
    );
  }

  return (
    <div style={FINANCE_SCREEN_STYLE}>
      <FinanceBackButton onClick={onBack} />

      <div style={FINANCE_HEADER_STYLE}>
        <h1 style={FINANCE_TITLE_STYLE}>{title}</h1>
        <p style={FINANCE_SUBTITLE_STYLE}>{subtitle}</p>
      </div>

      {error ? (
        <p style={{ color: "#f87171", marginBottom: "12px" }}>{error}</p>
      ) : null}

      {loading ? (
        <p style={{ opacity: 0.7 }}>Loading…</p>
      ) : (
        <form
          onSubmit={handleSubmit}
          style={{
            ...FINANCE_CARD_STYLE,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            maxWidth: "480px",
          }}
        >
          <CPDropdown
            label="Account"
            value={accountId}
            onChange={setAccountId}
            required={true}
            options={[
              { value: "", label: "Select account" },
              ...accounts.map((account) => ({
                value: account.id,
                label: account.name,
              })),
            ]}
          />

          <label>
            <span style={FINANCE_LABEL_STYLE}>Amount</span>
            <input
              style={FINANCE_INPUT_STYLE}
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </label>

          <label>
            <span style={FINANCE_LABEL_STYLE}>Date</span>
            <input
              style={FINANCE_INPUT_STYLE}
              type="date"
              value={occurredOn}
              onChange={(e) => setOccurredOn(e.target.value)}
              required
            />
          </label>

          <label>
            <span style={FINANCE_LABEL_STYLE}>Description</span>
            <input
              style={FINANCE_INPUT_STYLE}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                isInbound
                  ? "Customer payment, deposit, interest…"
                  : "Office expense, fuel, subscription…"
              }
            />
          </label>

          <CPDropdown
            label="Category (optional)"
            value={categoryId}
            onChange={setCategoryId}
            options={[
              { value: "", label: "None" },
              ...filteredCategories.map((category) => ({
                value: category.id,
                label: category.name,
              })),
            ]}
          />

          <label>
            <span style={FINANCE_LABEL_STYLE}>Notes (optional)</span>
            <textarea
              style={{ ...FINANCE_INPUT_STYLE, minHeight: "72px" }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              type="submit"
              style={FINANCE_PRIMARY_BTN_STYLE}
              disabled={busy || accounts.length === 0}
            >
              {busy ? "Saving…" : `Save ${title}`}
            </button>
            <button
              type="button"
              style={FINANCE_SECONDARY_BTN_STYLE}
              onClick={onBack}
              disabled={busy}
            >
              Cancel
            </button>
          </div>

          {accounts.length === 0 ? (
            <p style={{ margin: 0, opacity: 0.65, fontSize: "13px" }}>
              Create an account first before recording money.
            </p>
          ) : null}
        </form>
      )}
    </div>
  );
}
